import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText as FiFileText } from 'lucide-react';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import StatusBadge from '../components/StatusBadge';
import EditableCell from '../components/EditableCell';
import LoadingState from '../components/LoadingState';
import {
  AccountsPayableRecord,
  getAccountsPayableById,
  isCreditReference,
  listAccountsPayablePayments,
  patchAccountsPayable,
  updateAccountsPayableWorkflow,
} from '../lib/accountsPayableApi';
import { billActionRefusalMessage, billVouchers } from '../lib/accountsPayablePaymentsApi';
import type { BillVoucherRef } from '../lib/accountsPayablePaymentsApi';
import { getVendorInvoiceById, fetchVendorInvoiceAttachmentUrl } from '../lib/vendorInvoiceApi';
import { formatDate as fmtDate, formatDateTime as fmtDateTime, storedDay } from "../utils/dateFormat";
import { downloadObjectsCsv } from '../utils/downloadCsv';
import { billSourceLabel, billStatusLabel } from '../features/accountsPayableReports/billLabels';
// A bill's money: TT$ / US$ from the label owner, never the locale's bare "$" (lane 5, V3d).
import { formatBillMoney as formatMoney } from '../features/accountsPayableReports/format';
import { lineDiscountAmount, computeLineMath } from '../utils/invoiceLineMath';
import DateInput from "../components/DateInput";

function formatDate(value?: string | null) {
  return fmtDate(value, '--');
}

// An invoice date, a due date and a payment date are stored CALENDAR DAYS (UTC midnight): print their own day, not
// the browser-zone reading of the instant (the 21st showed as the 20th in Trinidad, D109.6).
function formatStoredDay(value?: string | null) {
  return fmtDate(storedDay(value), '--');
}

function formatDateTime(value?: string | null) {
  return fmtDateTime(value, '--');
}

/** Where the due date came from, in the reviewer's words. Blank when stated. */
function dueDateHint(source?: AccountsPayableRecord['dueDateSource']) {
  if (source === 'terms') return "Calculated from the invoice date and the invoice's payment terms";
  if (source === 'vendor_terms') return "Assumed from the supplier's credit days — the invoice stated none";
  return undefined;
}


function getApiBaseUrl(): string {
  const w = window as any;
  const injected = typeof w.__API_BASE_URL__ === 'string' ? w.__API_BASE_URL__.trim() : '';
  if (injected) return injected.replace(/\/+$/, '');

  const origin = window.location.origin;
  const isLocal3000 = /localhost:3000$/i.test(origin) || /127\.0\.0\.1:3000$/i.test(origin);
  if (isLocal3000) return 'http://localhost:3001';
  return origin.replace(/\/+$/, '');
}

const INFO = (
  <div className="space-y-2">
    <p>This page provides invoice-level AP detail, payment history, and workflow controls.</p>
    <p>
      Use it for recording payments, disputes, holds, and audit review. A bill is only ever paid by a payment
      voucher; to undo a payment, void its voucher.
    </p>
  </div>
);

/** A payment reference as this page reads it: `paymentId` links the voucher it was written for (D49). */
type PaymentRef = NonNullable<AccountsPayableRecord['paymentReferences']>[number] & { paymentId?: string };

export const AccountsPayableDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [row, setRow] = useState<AccountsPayableRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentRows, setPaymentRows] = useState<any[]>([]);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [workflowNote, setWorkflowNote] = useState('');
  const [workflowBusy, setWorkflowBusy] = useState(false);
  // A refused Mark Pending (or other workflow step), explained by its CODE, with the
  // payment vouchers behind the bill when that is why (D49 phase 5).
  const [workflowRefusal, setWorkflowRefusal] = useState<{ message: string; vouchers: BillVoucherRef[] } | null>(null);
  const [vendorLineItems, setVendorLineItems] = useState<any[]>([]);
  const [vendorInvoice, setVendorInvoice] = useState<any | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setPaymentLoading(true);
    try {
      const [apRes, payRes] = await Promise.all([
        getAccountsPayableById(id),
        listAccountsPayablePayments({ page: 1, limit: 100 }),
      ]);
      const ap = apRes.data;
      setRow(ap);
      setVendorInvoice(null);
      setPaymentRows(
        (payRes.data.items || []).filter((payment) =>
          (payment.allocations || []).some((allocation) => allocation.accountsPayableId === id)
        )
      );
      if (ap.vendorInvoiceId) {
        try {
          const vendorRes = await getVendorInvoiceById(ap.vendorInvoiceId);
          setVendorInvoice(vendorRes.data || null);
          const items = Array.isArray(vendorRes.data?.extracted?.lineItems) ? vendorRes.data.extracted.lineItems : [];
          setVendorLineItems(items.length > 0 ? items : Array.isArray(ap.lineItems) ? ap.lineItems : []);
        } catch {
          setVendorLineItems(Array.isArray(ap.lineItems) ? ap.lineItems : []);
        }
      } else {
        setVendorLineItems(Array.isArray(ap.lineItems) ? ap.lineItems : []);
      }
    } catch {
      setRow(null);
      setPaymentRows([]);
      setVendorLineItems([]);
    } finally {
      setLoading(false);
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const auditRows = useMemo(() => [...(row?.auditTrail || [])].reverse(), [row]);

  async function savePatch(payload: Record<string, unknown>) {
    if (!id) return;
    setSaving(true);
    try {
      await patchAccountsPayable(id, payload);
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function doWorkflow(
    payload: { status?: any },
    note?: string
  ) {
    if (!id) return;
    setWorkflowBusy(true);
    setWorkflowRefusal(null);
    try {
      await updateAccountsPayableWorkflow(id, {
        ...payload,
        // No actor: the server records the signed-in user (D44 VR-2 — this sent a
        // hardcoded 'super admin' into the audit trail).
        note: note || workflowNote,
      });
      setWorkflowNote('');
      await load();
    } catch (e: unknown) {
      // By code, never the server's sentence. Mark Pending on a bill with payments
      // is refused with the vouchers to void; every other refusal gets its own line.
      setWorkflowRefusal({ message: billActionRefusalMessage(e), vouchers: billVouchers(e) });
    } finally {
      setWorkflowBusy(false);
    }
  }

  // A voided payment is still a record but no longer money paid: it stays out of the
  // count and the allocated total (D49 phase 5) and is counted apart.
  const paymentSummary = useMemo(() => {
    const live = paymentRows.filter((payment) => payment.status !== 'void');
    const allocated = live.reduce((sum, payment) => {
      const alloc = (payment.allocations || []).reduce((inner: number, item: any) => {
        if (item.accountsPayableId !== id) return inner;
        return inner + Number(item.amount || 0);
      }, 0);
      return sum + alloc;
    }, 0);
    return { paymentCount: live.length, voidedCount: paymentRows.length - live.length, allocated };
  }, [id, paymentRows]);

  // Split paymentReferences into credits vs regular payments. Credits are the
  // vendor returns an Admin approved (D44), a vendor credit balance applied here,
  // and credit notes typed on the bill before D44 removed that.
  const creditNoteRefs = useMemo(
    () => (row?.paymentReferences || []).filter((r) => isCreditReference(r)),
    [row]
  );
  const regularPaymentRefs = useMemo(
    () => ((row?.paymentReferences || []) as PaymentRef[]).filter((r) => !isCreditReference(r)),
    [row]
  );
  // Only credits the server applied to the balance count as applied: a credit
  // note recorded before AP-0 never lowered outstanding (D31), and a reversed
  // vendor return was taken back off it.
  const appliedCreditRefs = useMemo(
    () => creditNoteRefs.filter((r) => r.appliedToBalance === true),
    [creditNoteRefs]
  );
  const totalCreditApplied = useMemo(
    () => appliedCreditRefs.reduce((s, r) => s + Number(r.amount || 0), 0),
    [appliedCreditRefs]
  );

  const exportRows = useMemo(
    () => [
      {
        Type: 'Invoice',
        InvoiceNumber: row?.invoiceNumber || '',
        Supplier: row?.supplierName || '',
        InvoiceDate: formatStoredDay(row?.invoiceDate),
        DueDate: formatStoredDay(row?.dueDate),
        TotalAmount: row?.totalAmount ?? 0,
        PaidAmount: row?.paidAmount ?? 0,
        OutstandingAmount: row?.outstandingAmount ?? 0,
        Status: row?.status || '',
        Notes: row?.notes || '',
      },
      ...auditRows.map((audit) => ({
        Type: 'Audit',
        InvoiceNumber: row?.invoiceNumber || '',
        Supplier: row?.supplierName || '',
        InvoiceDate: '',
        DueDate: formatDate(audit.at),
        TotalAmount: '',
        PaidAmount: '',
        OutstandingAmount: '',
        Status: `${audit.previousStatus || ''} -> ${audit.nextStatus || ''}`,
        Action: audit.action || '',
        Notes: audit.note || '',
      })),
    ],
    [auditRows, row]
  );

  const displayedWorkflowNote = row?.workflowNote || row?.disputeNote || row?.holdNote || row?.notes || '';
  // Direct (non-invoice) attachment URLs only. The vendor-invoice attachment
  // route is signature-gated now, so that path is minted on click instead —
  // see openAttachment below.
  const directAttachmentUrl = useMemo(() => {
    const source = vendorInvoice?.source || (row as any)?.source || {};
    const candidates = [source.gcsUrl, source.fileUrl, source.filePath];

    for (const candidate of candidates) {
      const raw = String(candidate || '').trim();
      if (!raw) continue;
      if (/^https?:\/\//i.test(raw)) return raw;
      if (raw.startsWith('/files/')) return `${getApiBaseUrl()}${raw}`;
    }
    return '';
  }, [row, vendorInvoice]);

  const hasAttachment = Boolean(row?.vendorInvoiceId || directAttachmentUrl);

  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const openAttachment = async () => {
    setAttachmentError(null);
    try {
      if (row?.vendorInvoiceId) {
        const signedPath = await fetchVendorInvoiceAttachmentUrl(String(row.vendorInvoiceId));
        window.open(`${getApiBaseUrl()}${signedPath}`, '_blank', 'noopener,noreferrer');
        return;
      }
      if (directAttachmentUrl) {
        window.open(directAttachmentUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (e: any) {
      // The link is minted server-side. A button that silently does nothing
      // when that fails reads as broken; say so, by code, next to the button.
      const code = typeof e?.code === 'string' ? e.code : '';
      setAttachmentError(
        code === 'NOT_FOUND'
          ? 'The invoice this payable came from has no attachment on file.'
          : 'The attachment link could not be created — try again, or open the invoice from Vendor Invoices.'
      );
    }
  };

  if (loading) {
    return (
      <div className="h-full bg-brand-bg">
        <LoadingState text="Loading AP detail..." />
      </div>
    );
  }

  if (!row) {
    return (
      <div className="h-full flex items-center justify-center bg-brand-bg text-slate-600">
        AP record not found.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <div className="px-8 pt-4 pb-3 bg-brand-bg border-b border-slate-200">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/accounts')}
              className="inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft aria-hidden="true" className="h-4 w-4" />
              Back
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold leading-7 text-slate-900">
                  AP Invoice {row.invoiceNumber || '--'}
                </h1>
                <ModuleInfoButton content={INFO} ariaLabel="AP detail information" />
              </div>
              <div className="text-xs text-slate-500">
                Supplier: {row.supplierName || '--'} · Source: {billSourceLabel(row.source?.kind)} · Status:{' '}
                {billStatusLabel(row.status)}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasAttachment ? (
              <button
                onClick={openAttachment}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
              >
                <FiFileText />
                View Attachment
              </button>
            ) : null}
            {attachmentError ? (
              <span role="alert" className="text-xs text-rose-700">{attachmentError}</span>
            ) : null}
            {row.vendorInvoiceId ? (
              <button
                onClick={() => navigate(`/credit-notes/vendor/new?vendorInvoiceId=${row.vendorInvoiceId}`)}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-purple-200 bg-purple-50 text-purple-700 text-sm font-semibold hover:bg-purple-100"
              >
                Raise vendor return
              </button>
            ) : null}
            <button
              onClick={() => downloadObjectsCsv(`ap-${row.invoiceNumber || row._id}.csv`, exportRows)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 py-4 flex-1 overflow-auto">
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
          <Card label="Total Amount" value={formatMoney(row.totalAmount, row.currency || 'TTD')} />
          <Card label="Paid Amount" value={formatMoney(row.paidAmount, row.currency || 'TTD')} />
          <Card label="Outstanding" value={formatMoney(row.outstandingAmount, row.currency || 'TTD')} />
          <Card label="VAT" value={formatMoney(row.vatAmount, row.currency || 'TTD')} />
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
          <Card
            label="Payment Records"
            value={
              <>
                {paymentSummary.paymentCount}
                {paymentSummary.voidedCount > 0 ? (
                  <span className="ml-2 text-xs font-medium text-rose-700">{paymentSummary.voidedCount} voided</span>
                ) : null}
              </>
            }
          />
          <Card label="Allocated To This AP" value={formatMoney(paymentSummary.allocated, row.currency || 'TTD')} />
          <Card
            label="Credits Applied"
            value={
              <span className="text-purple-700">
                {appliedCreditRefs.length > 0
                  ? `${appliedCreditRefs.length} · ${formatMoney(totalCreditApplied, row.currency || 'TTD')}`
                  : 'None'}
              </span>
            }
          />
          <Card label="Last Synced" value={formatDate(row.lastSyncedAt)} />
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4 mb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="text-sm font-semibold text-slate-800">Invoice / Supplier</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Supplier Name" value={row.supplierName || ''} />
                <Field label="Supplier Contact" value={row.supplierContact || ''} />
                <Field label="Invoice Number" value={row.invoiceNumber || ''} />
                <Field label="Currency" value={row.currency || 'TTD'} />
              </div>
            </div>
            <div className="space-y-3">
              <div className="text-sm font-semibold text-slate-800">Workflow</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field
                  label="AP Status"
                  value={billStatusLabel(row.status || 'unpaid')}
                  editable={false}
                />
                <Field
                  label="Due Date"
                  value={storedDay(row.dueDate) ?? ''}
                  // A due date is a stored calendar day; it is read as dd/mm/yyyy, and edited as the
                  // date input's own yyyy-mm-dd.
                  display={row.dueDate ? fmtDate(storedDay(row.dueDate), '--') : ''}
                  editable
                  onSave={(value) => savePatch({ dueDate: value })}
                  type="date"
                  // Suppliers print terms, not a due date, so most of these are
                  // calculated. Say so — this is the date the payment run
                  // chases, and a worked-out one must not read as a stated one.
                  hint={dueDateHint(row.dueDateSource)}
                />
                <Field
                  label="Workflow Note"
                  value={displayedWorkflowNote}
                  editable
                  onSave={(value) => savePatch({ workflowNote: value, notes: value })}
                  type="text"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => void doWorkflow({ status: 'unpaid' }, 'Marked pending')}
              disabled={workflowBusy}
              className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Mark Pending
            </button>
            {/* D49 phase 5: there is no Mark Paid. A bill is paid only by a payment
                voucher, so this hands the person to Record payment for THIS bill: the
                Accounts page opens its Record payment window on the `pay` bill. */}
            <button
              onClick={() => navigate(`/accounts?tab=payable&pay=${row._id}`)}
              disabled={workflowBusy || (typeof row.outstandingAmount === 'number' && row.outstandingAmount <= 0.005)}
              title={
                typeof row.outstandingAmount === 'number' && row.outstandingAmount <= 0.005
                  ? 'Nothing is owed on this bill.'
                  : 'Opens Record payment for this bill'
              }
              className="px-3 py-2 rounded-md border border-cyan-200 bg-cyan-50 text-xs font-semibold text-cyan-700 hover:bg-cyan-100 disabled:opacity-50"
            >
              Record a payment
            </button>
            <button
              onClick={() => void doWorkflow({ status: 'on_hold' }, workflowNote)}
              disabled={workflowBusy}
              className="px-3 py-2 rounded-md border border-amber-200 bg-amber-50 text-xs font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"
            >
              Put On Hold
            </button>
            <button
              onClick={() => void doWorkflow({ status: 'disputed' }, workflowNote)}
              disabled={workflowBusy}
              className="px-3 py-2 rounded-md border border-rose-200 bg-rose-50 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
            >
              Mark Disputed
            </button>
            <input
              value={workflowNote}
              onChange={(e) => setWorkflowNote(e.target.value)}
              placeholder="Workflow note"
              aria-label="Workflow note"
              className="min-w-[240px] flex-1 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm outline-none focus:border-cyan-300"
            />
          </div>
          {workflowRefusal ? (
            <div role="alert" className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <div>{workflowRefusal.message}</div>
              {workflowRefusal.vouchers.length > 0 ? (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-amber-800">
                    Open each payment and use Void payment, then mark this bill pending:
                  </div>
                  <ul className="mt-1 space-y-1">
                    {workflowRefusal.vouchers.map((v) => (
                      <li key={v.id}>
                        <button
                          type="button"
                          className="font-semibold underline underline-offset-2 hover:text-amber-950"
                          onClick={() => navigate(`/accounts/payments/${v.id}`)}
                        >
                          {v.reference || 'Payment voucher'}
                        </button>
                        <span className="text-xs text-amber-800">
                          {' '}
                          {formatStoredDay(v.paymentDate)} · {formatMoney(v.amount, row.currency || 'TTD')}
                          {typeof v.allocatedToBill === 'number' && v.allocatedToBill !== v.amount
                            ? ` (${formatMoney(v.allocatedToBill, row.currency || 'TTD')} on this bill)`
                            : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200">
              <div className="text-sm font-semibold text-slate-800">Payment References</div>
            </div>
            <div className="overflow-auto">
              <table className="data-table data-table-grid min-w-[700px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs text-slate-600">
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Method</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {regularPaymentRefs.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        No payments recorded.
                      </td>
                    </tr>
                  )}
                  {regularPaymentRefs.map((ref, idx) => {
                    // A voided payment's reference is kept, marked, never deleted (D49 phase 5).
                    const voided = Boolean(ref.reversedAt);
                    return (
                      <tr
                        key={`${ref.reference || 'ref'}-${idx}`}
                        data-voided={voided ? 'true' : 'false'}
                        className={`border-b border-slate-100 ${voided ? 'text-slate-400' : ''}`}
                      >
                        <td className="px-4 py-3">
                          {ref.paymentId ? (
                            <button
                              type="button"
                              className="underline underline-offset-2 hover:text-cyan-700"
                              onClick={() => navigate(`/accounts/payments/${ref.paymentId}`)}
                            >
                              {ref.reference || '--'}
                            </button>
                          ) : (
                            ref.reference || '--'
                          )}
                        </td>
                        <td className="px-4 py-3">{formatStoredDay(ref.paymentDate)}</td>
                        <td className={`px-4 py-3 whitespace-nowrap ${voided ? 'line-through' : ''}`}>
                          {formatMoney(ref.amount, row.currency || 'TTD')}
                        </td>
                        <td className="px-4 py-3">{ref.method || '--'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {voided ? (
                            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-800">
                              Voided {formatDate(ref.reversedAt)}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-500">Applied</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{ref.note || '--'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-purple-100 rounded-md shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-purple-100">
              <div className="text-sm font-semibold text-slate-800">Credits</div>
              <div className="text-xs text-slate-500 mt-0.5">
                A supplier credit comes from a vendor return an Admin approved.
                {creditNoteRefs.length > 0 && (
                  <span className="text-purple-600"> Total applied: {formatMoney(totalCreditApplied, row.currency || 'TTD')}</span>
                )}
              </div>
            </div>
            <div className="overflow-auto">
              <table className="data-table data-table-grid min-w-[700px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs text-slate-600">
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {creditNoteRefs.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                        No credits on this bill.
                      </td>
                    </tr>
                  )}
                  {creditNoteRefs.map((ref, idx) => (
                    <tr key={`credit-${ref.reference || idx}-${idx}`} className="border-b border-slate-100">
                      <td className="px-4 py-3 font-medium text-purple-700">
                        {ref.debitNoteId ? (
                          <button
                            type="button"
                            className="underline underline-offset-2 hover:text-purple-900"
                            onClick={() => navigate(`/credit-notes/vendor/${ref.debitNoteId}`)}
                          >
                            {ref.reference || 'Vendor return'}
                          </button>
                        ) : (
                          ref.reference || '--'
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">{creditTypeLabel(ref.method)}</td>
                      <td className="px-4 py-3">{formatStoredDay(ref.paymentDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-semibold text-purple-700">
                        −{formatMoney(ref.amount, row.currency || 'TTD')}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {ref.note || '--'}
                        {ref.reversedAt ? (
                          <div className="mt-1 font-semibold text-amber-700">Reversed {formatDate(ref.reversedAt)}: no longer applied</div>
                        ) : ref.appliedToBalance !== true ? (
                          // Recorded before AP-0: stored, never lowered outstanding,
                          // and not applied retroactively (D31).
                          <div
                            className="mt-1 font-semibold text-amber-700"
                            title="Recorded before credit notes lowered the balance. Outstanding does not include it."
                          >
                            Not applied to balance
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200">
              <div className="text-sm font-semibold text-slate-800">Audit Trail</div>
            </div>
            <div className="overflow-auto max-h-[320px]">
              <table className="data-table data-table-grid min-w-[700px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs text-slate-600">
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">From</th>
                  <th className="px-4 py-3">To</th>
                  <th className="px-4 py-3">Note</th>
                    <th className="px-4 py-3">At</th>
                  </tr>
                </thead>
                <tbody>
                  {auditRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                        No audit entries yet.
                      </td>
                    </tr>
                  )}
                  {auditRows.map((entry, idx) => (
                    <tr key={`${entry.at || idx}`} className="border-b border-slate-100">
                      <td className="px-4 py-3">{entry.action || '--'}</td>
                      <td className="px-4 py-3">{entry.previousStatus || '--'}</td>
                      <td className="px-4 py-3">{entry.nextStatus || '--'}</td>
                      <td className="px-4 py-3">{entry.note || '--'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(entry.at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200">
              <div className="text-sm font-semibold text-slate-800">Full AP Snapshot</div>
            </div>
            <div className="overflow-auto">
            <table className="data-table data-table-grid min-w-[1000px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs text-slate-600">
                  <th className="px-4 py-3">Invoice Date</th>
                  <th className="px-4 py-3">Due Date</th>
                  <th className="px-4 py-3">Total Amount</th>
                  <th className="px-4 py-3">VAT</th>
                  <th className="px-4 py-3">Paid</th>
                  <th className="px-4 py-3">Outstanding</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Workflow Note</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3">{formatStoredDay(row.invoiceDate)}</td>
                  <td className="px-4 py-3">{formatStoredDay(row.dueDate)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatMoney(row.totalAmount, row.currency || 'TTD')}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatMoney(row.vatAmount, row.currency || 'TTD')}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatMoney(row.paidAmount, row.currency || 'TTD')}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatMoney(row.outstandingAmount, row.currency || 'TTD')}</td>
                  <td className="px-4 py-3">{row.status ? <StatusBadge status={String(row.status)} /> : '--'}</td>
                  <td className="px-4 py-3">{displayedWorkflowNote || '--'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200">
              <div className="text-sm font-semibold text-slate-800">Invoice Line Items</div>
            <div className="text-xs text-slate-500 mt-1">
              These fields are sourced directly from the vendor invoice line-item payload.
            </div>
          </div>
          <div className="overflow-auto">
            <table className="data-table data-table-grid data-table-dense min-w-[1700px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs text-slate-600">
                  <th className="px-4 py-3">Line #</th>
                  <th className="px-4 py-3">Item Name</th>
                  <th className="px-4 py-3">Item Code</th>
                  <th className="px-4 py-3">Pack Unit</th>
                  <th className="px-4 py-3">Quantity</th>
                  <th className="px-4 py-3">Total Units</th>
                  {/* One column, not two: "Unit Size" already carries the unit
                      ("500 ML"), so a separate "Unit" column printed ML twice
                      on the same row. Kept in step with the review grid on
                      VendorInvoicesPage. */}
                  <th className="px-4 py-3">Unit Size</th>
                  <th className="px-4 py-3">Unit Price</th>
                  <th className="px-4 py-3">Discount</th>
                  <th className="px-4 py-3">VAT %</th>
                  <th className="px-4 py-3">VAT Amount</th>
                  <th className="px-4 py-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {(vendorLineItems || []).length === 0 ? (
                  <tr>
                    {/* 12 columns after HSN was dropped and Unit merged into
                        Unit Size — keep in step with the thead above. */}
                    <td colSpan={12} className="px-4 py-8 text-center text-slate-500">
                      No line items available for this invoice.
                    </td>
                  </tr>
                ) : (
                  vendorLineItems.map((item: any, idx: number) => (
                    <tr key={`${item.description || 'line'}-${idx}`} className="border-b border-slate-100">
                      <td className="px-4 py-3">
                        <div className="text-slate-500">{item.lineNo ?? item.lineNumber ?? idx + 1}</div>
                      </td>
                      <td className="px-4 py-3">
                        {/* itemName first, description as the fallback: this page is
                            read-only and never triggers the backfill, so a line that
                            has not been opened in the review grid yet still shows
                            something rather than a dash. */}
                        <div className="font-medium text-slate-800">
                          {item.itemName || item.description || item.name || '--'}
                        </div>
                      </td>
                      <td className="px-4 py-3">{item.itemCode || item.sku || item.itemNumber || '--'}</td>
                      <td className="px-4 py-3">{item.splitUow || item.uow || item.uom || item.unit || '--'}</td>
                      <td className="px-4 py-3">{item.splitQuantity ?? item.qty ?? item.quantity ?? '--'}</td>
                      <td className="px-4 py-3">{item.splitItemUnits ?? item.itemUnits ?? item.packCount ?? '--'}</td>
                      <td className="px-4 py-3">
                        {/* Falls back to composing the two stored fields for a
                            line processed before they were merged. */}
                        {item.splitUnitSize ||
                          item.unitSize ||
                          [item.unitSizeValue, item.splitItemUow || item.itemUow]
                            .filter(Boolean)
                            .join(' ') ||
                          '--'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatMoney(Number(item.unitPrice ?? item.price ?? 0), row.currency || 'TTD')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {/* D83 (prod vendor-invoice 3723225809, Blue Waters): `discount`
                            is stored exactly as printed — a per-case amount, a net
                            price, a percent — so the total this line actually loses
                            is `lineDiscountAmount`, shown here with the printed figure
                            beneath it whenever the two differ; an unproven line, or one
                            already printed as a whole-line amount, shows just the one
                            figure, unchanged. */}
                        {(() => {
                          const total = lineDiscountAmount(item);
                          const printed =
                            item.discount !== undefined && item.discount !== null ? Number(item.discount) : null;
                          if (printed === null) return '--';
                          if (total === null || total === printed) return formatMoney(printed, row.currency || 'TTD');
                          // A PERCENT-proven line's raw `discount` is a RATE, not
                          // money (D83 redesign, item C) — "10" as printed means
                          // 10%, and formatting it as currency states a figure
                          // the invoice never printed.
                          const printedAsRate = computeLineMath(item).provenReading === 'percent';
                          return (
                            <>
                              <div>{formatMoney(total, row.currency || 'TTD')}</div>
                              <div className="text-xs text-slate-400">
                                {printedAsRate ? `${printed}%` : formatMoney(printed, row.currency || 'TTD')} as printed
                              </div>
                            </>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {item.taxRate !== undefined || item.vatPercent !== undefined
                          ? `${item.taxRate ?? item.vatPercent ?? item.vat ?? ''}${item.taxRate || item.vatPercent || item.vat ? '%' : ''}`
                          : '--'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {item.taxAmount !== undefined || item.vatAmount !== undefined
                          ? formatMoney(Number(item.taxAmount ?? item.vatAmount ?? 0), row.currency || 'TTD')
                          : '--'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatMoney(Number(item.lineTotal ?? item.total ?? 0), row.currency || 'TTD')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
};

/** What kind of credit a payment reference is, in words. */
function creditTypeLabel(method?: string) {
  if (method === 'debit_note') return 'Vendor return';
  if (method === 'vendor_credit') return 'Vendor credit balance';
  return 'Credit note (typed before D44)';
}

function Card({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function Field({
  label,
  value,
  editable,
  onSave,
  type = 'text',
  hint,
  display,
}: {
  label: string;
  value: string;
  /** What is printed when the field is not being edited, when that differs from the stored value (a date). */
  display?: string;
  editable?: boolean;
  onSave?: (value: string) => Promise<void> | void;
  type?: 'text' | 'date';
  /** Provenance, not help text — where a value came from when it was not typed. */
  hint?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  // D109.6: false while the date box holds a refused date; `draft` is then the OLD date, so Save is blocked.
  const [dateValid, setDateValid] = useState(true);

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  const hintLine = hint ? (
    <div className="mt-1 text-xs italic text-slate-400">{hint}</div>
  ) : null;

  if (!editable) {
    return (
      <div className="rounded-md border border-slate-200 p-3">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="mt-1 text-sm text-slate-800">{display || value || '--'}</div>
        {hintLine}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      {hintLine}
      {editing ? (
        <div className="mt-2 flex items-center gap-2">
          {type === 'date' ? (
            <DateInput
              value={draft}
              onChange={setDraft}
              validity={setDateValid}
              className="flex-1 h-9 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
            />
          ) : (
            <input
              type={type}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="flex-1 h-9 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-cyan-300"
            />
          )}
          <button
            disabled={!dateValid}
            title={!dateValid ? 'Fix the date first' : undefined}
            onClick={async () => {
              if (!dateValid) return;
              await onSave?.(draft);
              setEditing(false);
            }}
            className="px-3 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Save
          </button>
          <button
            onClick={() => {
              setDraft(value || '');
              setEditing(false);
            }}
            className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="mt-1 text-sm text-slate-800 text-left w-full hover:text-cyan-700"
        >
          {display || value || '--'}
        </button>
      )}
    </div>
  );
}
